import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { convertDatesInObject, insertAtBeginning } from '@/helpers/api'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { schedulesQueryKeys } from '@/hooks/schedules/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { CreateSchedule, Schedule } from '@/types/Schedule'

interface CreateScheduleArgs {
  orgId: UUID | ''
}

const createSchedule = async (
  schedule: CreateSchedule,
  accessToken: string,
): Promise<Schedule> => {
  const res: Response = await fetch(`${API_URL}/api/v1/schedules/`, {
    method: 'POST',
    body: JSON.stringify(schedule),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json()) as Schedule
}

export const useCreateSchedule = (args: CreateScheduleArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<Schedule, Error, CreateSchedule, void>({
    mutationFn: (schedule) => createSchedule(schedule, accessToken),
    onError: (err, schedule, context) => {
      // rollback to the previous value
      // TODO: figure out how to properly handle this error, since it propagates to the user
      const key = schedulesQueryKeys.organization({ orgId: args.orgId })
      queryClient.setQueryData(key, context)
      handleError(err)
    },
    onSuccess: (data) => {
      const key = schedulesQueryKeys.organization({ orgId: args.orgId })
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<Schedule>, PaginateRequest>
      >(key, (oldData) => insertAtBeginning(data, oldData))
      queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
